import type { TFunction } from 'i18next';

import { FAQ_KEYS } from '@/features/pricing';
import { localizedPath } from '@/shared/config/locales';
import type { AppLocale } from '@/shared/lib';

import type { JsonLd } from './headTags';
import { faqPage, organization, softwareApplication, webSite } from './schema';
import { absoluteUrl } from './site';

/** Pages with registry-driven tags; the master showcase builds its own. */
export type SeoPage =
  | 'home'
  | 'features'
  | 'pricing'
  | 'explore'
  | 'about'
  | 'blog'
  | 'contact'
  | 'privacy'
  | 'terms'
  | 'support'
  | 'rules'
  | 'reviews'
  | 'updates'
  | 'notFound'
  | 'app'
  | 'client'
  | 'account';

/** Pages search engines must not index: private areas, sign-in and the 404. */
export const NOINDEX_PAGES: ReadonlySet<SeoPage> = new Set([
  'notFound',
  'app',
  'client',
  'account',
]);

type SchemaContext = { t: TFunction; locale: AppLocale };

const product = ({ t }: SchemaContext) =>
  softwareApplication(t('seo:organization'), (tier) => t(`marketing:pricing.plans.${tier}.name`));

const faq = ({ t }: SchemaContext) =>
  faqPage(
    FAQ_KEYS.map((key) => ({
      question: t(`marketing:pricing.faq.${key}.q`),
      answer: t(`marketing:pricing.faq.${key}.a`),
    })),
  );

/** Structured data per page, limited to what that page actually shows. */
export const PAGE_SCHEMAS: Partial<Record<SeoPage, (context: SchemaContext) => JsonLd[]>> = {
  home: (context) => [
    organization(context.t('seo:organization')),
    webSite(absoluteUrl(localizedPath(context.locale, '/')), context.locale),
    product(context),
    faq(context),
  ],
  features: (context) => [product(context)],
  pricing: (context) => [product(context), faq(context)],
};
